import { Observable } from "rxjs";
import { Injectable, Injector } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { environment } from "../../environments/environment";
import { AuthService } from "@auth0/auth0-angular";
import { filter, switchMap, take } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";
import { DialogService } from "../../core/dialog/dialog.service";
//import { controlNameBinding } from '@angular/forms/src/directives/reactive_directives/form_control_name';

@Injectable()
export class LogService {
  private static readonly CLIENT_ACTIVITY_DUPLICATE_WINDOW_MS = 10000;
  private static readonly CLIENT_ACTIVITY_MAX_POSTS_PER_MINUTE = 20;

  level: number = LogLevel.Off;
  logWithDate: boolean = true;

  private apiUrl: string = environment.API_BASE_URL + "/player";
  private recentClientActivityPosts = new Map<string, number>();
  private clientActivityWindowStartedAt = 0;
  private clientActivityPostCount = 0;

  constructor(private authHttp: HttpClient, private injector: Injector) {
    if (this.isPublicGuestPath()) {
      return;
    }

    this.auth().isAuthenticated$.pipe(
      filter((isAuthenticated) => isAuthenticated),
      take(1),
      switchMap(() => this.getLogLevel())
    ).subscribe(
      (level) => {
        this.level = level;
      },
      (error) => {
        console.log(error);
      }
    );
  }

  public logClientActivity(body: Object) {
    if (!this.shouldPostClientActivity(body)) {
      return;
    }

    // Diagnostics must never surface an unhandled error or retry a write after reconnect.
    this.authHttp.post(`${this.apiUrl}/logClientActivity`, body, this.getRequestOptions()).subscribe({error: () => {}});
  }

  public getLogLevel(): Observable<number> {
    return this.authHttp.get<number>(`${this.apiUrl}/getLogLevel`, this.getRequestOptions());
  }

  debug(msg: string, ...optionalParams: any[]) {
    this.writeToLog(msg, LogLevel.Debug, optionalParams);
  }

  info(msg: string, ...optionalParams: any[]) {
    this.writeToLog(msg, LogLevel.Info, optionalParams);
  }

  warn(msg: string, ...optionalParams: any[]) {
    this.writeToLog(msg, LogLevel.Warn, optionalParams);
  }

  error(msg: string, ...optionalParams: any[]) {
    this.writeToLog(msg, LogLevel.Error, optionalParams);
  }

  fatal(msg: string, ...optionalParams: any[]) {
    this.writeToLog(msg, LogLevel.Fatal, optionalParams);
  }

  log(msg: string, ...optionalParams: any[]) {
    this.writeToLog(msg, LogLevel.All, optionalParams);
  }

  private getRequestOptions() {
    return {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
    };
  }

  private isPublicGuestPath(): boolean {
    const path = this.currentPath();
    return path.indexOf("/guest-manager") === 0 ||
      path.indexOf("/join/") === 0 ||
      path.indexOf("/public-event-invite/") === 0 ||
      path.indexOf("/i/") === 0 ||
      path.indexOf("/claim/") === 0;
  }

  private currentPath(): string {
    if (window.location.hash && window.location.hash.indexOf("#/") === 0) {
      return window.location.hash.substring(1).split("?")[0];
    }

    return window.location.pathname || "/";
  }

  private auth(): AuthService {
    return this.injector.get(AuthService);
  }

  private translate(): TranslateService {
    return this.injector.get(TranslateService);
  }

  private dialog(): DialogService {
    return this.injector.get(DialogService);
  }

  private shouldPostClientActivity(body: Object): boolean {
    const now = Date.now();
    if (now - this.clientActivityWindowStartedAt > 60000) {
      this.clientActivityWindowStartedAt = now;
      this.clientActivityPostCount = 0;
    }

    if (this.clientActivityPostCount >= LogService.CLIENT_ACTIVITY_MAX_POSTS_PER_MINUTE) {
      return false;
    }

    const fingerprint = this.clientActivityFingerprint(body);
    const lastPostedAt = this.recentClientActivityPosts.get(fingerprint);
    if (lastPostedAt && now - lastPostedAt < LogService.CLIENT_ACTIVITY_DUPLICATE_WINDOW_MS) {
      return false;
    }

    this.recentClientActivityPosts.set(fingerprint, now);
    this.clientActivityPostCount++;
    this.pruneRecentClientActivityPosts(now);
    return true;
  }

  private clientActivityFingerprint(body: Object): string {
    return String(body || "")
      .replace(/Previous value: '[^']*'\\. Current value: '[^']*'\\./g, "Previous/current value changed.")
      .replace(/\\d+(\\.\\d+)?/g, "#")
      .slice(0, 1000);
  }

  private pruneRecentClientActivityPosts(now: number): void {
    this.recentClientActivityPosts.forEach((postedAt, fingerprint) => {
      if (now - postedAt > LogService.CLIENT_ACTIVITY_DUPLICATE_WINDOW_MS) {
        this.recentClientActivityPosts.delete(fingerprint);
      }
    });
  }

  private writeToLog(msg: string, level: LogLevel, params: any[]) {
    if (this.shouldLog(level)) {
      let player: string = localStorage.getItem("player");

      let value = player ? JSON.parse(player).email : "";
      value += " => ";
      if (this.logWithDate) {
        value += new Date() + " - ";
      }

      value += "Type: " + LogLevel[level];
      value += " - Message: " + msg;
      if (params.length) {
        value += " - Extra Info: " + this.formatParams(params);
      }

      try {
        console.log(value);
        this.logClientActivity(value);
      } catch (error) {
        console.log(error);
        if (error.message !== "No JWT present or has expired") {
          this.dialog().alert({
            title: this.translate().instant('common.alert'),
            text: this.translate().instant('errors.reportClientError', { details: error.stack })
          });
        }
      }
    }
  }

  private shouldLog(level: LogLevel): boolean {
    let ret: boolean = false;

    if ((level >= this.level && level !== LogLevel.Off) || this.level === LogLevel.All) {
      ret = true;
    }

    return ret;
  }

  private formatParams(params: any[]): string {
    let ret: string = params.join(",");

    // Is there at least one object in the array?
    if (params.some((p) => typeof p == "object")) {
      ret = "";
      for (let item of params) {
        ret += JSON.stringify(item) + ",";
      }
    }

    return ret;
  }
}

export enum LogLevel {
  All = 0,
  Debug = 1,
  Info = 2,
  Warn = 3,
  Error = 4,
  Fatal = 5,
  Off = 6,
}
